import mixpanel from 'mixpanel-browser';

import { datadogRum } from '@datadog/browser-rum';

import wait from 'src/utils/wait';

/** Also the shell's flag key, in its own Mixpanel project. */
export const NATIVE_AUTH_FLAG = 'native_auth';
const FIRST_FETCH_BUDGET_MS = 2_000;

/** `unknown` is no answer in time, kept apart from `off` so a slow network can't act as the kill
 * switch. */
export type NativeAuthFlag = 'on' | 'off' | 'unknown';

async function fetchVariantsInBudget(): Promise<boolean> {
    try {
        // Caps the login wait only; the fetch is not cancelled, and its answer is persisted for the
        // next load.
        return await Promise.race([
            mixpanel.flags.get_all_variants().then(() => true),
            wait(FIRST_FETCH_BUDGET_MS).then(() => false),
        ]);
    } catch (error) {
        datadogRum.addError(error, { context: 'readNativeAuthFlag' });
        return false;
    }
}

async function readFlag(): Promise<NativeAuthFlag> {
    // Undefined until `initMixpanel` runs with a token.
    if (!mixpanel.flags) {
        return 'off';
    }
    const startedAt = performance.now();
    const answered = await fetchVariantsInBudget();
    // The per-flag getters track `$experiment_started`; the bulk sync read does not.
    const isOn = mixpanel.flags.get_all_variants_sync().get(NATIVE_AUTH_FLAG)?.value === true;
    const result: NativeAuthFlag = !answered ? 'unknown' : isOn ? 'on' : 'off';
    datadogRum.addAction('Native Auth Flag Read', {
        result,
        durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
}

let decision: Promise<NativeAuthFlag> | undefined;

/**
 * Read once per page load so the auth mode can't change mid-session. The shell latches its side the
 * same way, and reloads the page to turn native auth off.
 */
export function readNativeAuthFlag(): Promise<NativeAuthFlag> {
    decision ??= readFlag();
    return decision;
}
